import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { ArrowRight, Users, Brain, Globe, BookOpen, GraduationCap, Lightbulb, Play, X, Sparkles, Wifi } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useStaticPageMeta } from '../lib/seo';

const HERO_IMG = "https://images.unsplash.com/photo-1741699427799-3fbb70fce948?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format";
const STUDENTS_IMG = "https://images.unsplash.com/photo-1760062744828-64801c56a039?crop=entropy&cs=srgb&fm=jpg&q=80&w=800&auto=format";

// Featured video: Sal Khan's TED Talk on AI in Education
const FEATURED_VIDEO = {
  id: "hJP5GqnTrNo",
  title: "How AI Could Save (Not Destroy) Education",
  speaker: "Sal Khan",
  duration: "15:42"
};

export default function Landing() {
  const { t } = useLanguage();
  useStaticPageMeta('home');
  const { user } = useAuth();
  const [showVideo, setShowVideo] = useState(false);
  const [welcomeName, setWelcomeName] = useState(null);

  useEffect(() => {
    try {
      const name = sessionStorage.getItem('ofa_welcome');
      if (name && user) {
        setWelcomeName(name);
        sessionStorage.removeItem('ofa_welcome');
      }
    } catch {}
  }, [user]);
  const AUDIENCES = [
    { icon: GraduationCap, title: t('landing.audience.students'), desc: t('landing.audience.students.desc'), color: '#2D78F6' },
    { icon: Users, title: t('landing.audience.educators'), desc: t('landing.audience.educators.desc'), color: '#926EF7' },
    { icon: Brain, title: t('landing.audience.adults'), desc: t('landing.audience.adults.desc'), color: '#2D78F6' },
    { icon: Globe, title: t('landing.audience.families'), desc: t('landing.audience.families.desc'), color: '#F8CD46' },
  ];
  const PILLARS = [
    { icon: BookOpen, title: t('landing.pillars.humanities'), desc: t('landing.pillars.humanities.desc'), color: '#2D78F6' },
    { icon: Globe, title: t('landing.pillars.languages'), desc: t('landing.pillars.languages.desc'), color: '#F8CD46' },
    { icon: Brain, title: t('landing.pillars.ai'), desc: t('landing.pillars.ai.desc'), color: '#926EF7' },
    { icon: Lightbulb, title: t('landing.pillars.formation'), desc: t('landing.pillars.formation.desc'), color: '#F8CD46' },
  ];

  return (
    <div data-testid="landing-page">
      {welcomeName && (
        <div data-testid="welcome-banner" className="fixed top-20 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-2rem)] animate-fade-up">
          <div className="bg-gradient-to-r from-[#14274B] to-[#2D78F6] text-white rounded-lg shadow-xl border border-white/20 px-5 py-3.5 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#F8CD46] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold leading-tight">{t('landing.welcome.title')}, {welcomeName} 👋</p>
              <p className="text-xs text-white/80 mt-0.5">{t('landing.welcome.subtitle')}</p>
            </div>
            <button
              onClick={() => setWelcomeName(null)}
              data-testid="close-welcome-banner"
              aria-label="Close welcome banner"
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            width="1600"
            height="900"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14274B]/70 via-[#14274B]/50 to-[#14274B]/15" />
        </div>
        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl py-32 md:py-40">
          <div className="max-w-2xl">
            <p className="text-sm font-medium tracking-widest uppercase text-[#2D78F6] mb-6 opacity-0 animate-fade-up" style={{ animationDelay: '0.1s' }}>{t('landing.badge')}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>{t('landing.hero.title')}</h1>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-[#F8CD46]/15 border border-[#F8CD46]/40 backdrop-blur-sm opacity-0 animate-fade-up" style={{ animationDelay: '0.25s' }} data-testid="online-badge">
              <Wifi className="w-3.5 h-3.5 text-[#F8CD46]" />
              <span className="text-xs md:text-sm font-medium text-white tracking-wide">{t('landing.online.pill')}</span>
            </div>
            <p className="text-lg md:text-xl font-light leading-relaxed text-slate-200 mb-10 max-w-xl opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>{t('landing.hero.subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-up" style={{ animationDelay: '0.4s' }}>
              <Link to="/programs" data-testid="hero-explore-programs" className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium bg-[#2D78F6] text-white rounded-sm hover:bg-[#1A5BB5] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                {t('landing.hero.cta')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/vision" data-testid="hero-learn-more" className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium bg-transparent border border-white/30 text-white rounded-sm hover:bg-white/10 transition-all duration-300">
                {t('landing.hero.vision')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-[#2D78F6] mb-4">{t('landing.about.badge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">{t('landing.about.title')}</h2>
              <p className="text-base font-normal leading-relaxed text-slate-600 mb-6">{t('landing.about.p1')}</p>
              <p className="text-base font-normal leading-relaxed text-slate-600 mb-8">{t('landing.about.p2')}</p>
              <Link to="/vision" className="inline-flex items-center gap-2 text-sm font-medium text-[#2D78F6] hover:text-[#1A5BB5] transition-colors duration-200">
                {t('landing.about.link')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={STUDENTS_IMG}
                alt="Students"
                className="rounded-lg shadow-xl w-full"
                loading="lazy"
                decoding="async"
                width="800"
                height="534"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#14274B] text-white px-6 py-4 rounded-sm border-l-4 border-[#F8CD46]">
                <p className="text-2xl font-bold font-heading">{t('landing.about.year')}</p>
                <p className="text-xs text-slate-300 tracking-wide">{t('landing.about.label')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-24 md:py-32" data-testid="video-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-[#2D78F6] mb-4">{t('landing.video.badge')}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">{t('landing.video.title')}</h2>
            <p className="text-base text-slate-600">{t('landing.video.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative aspect-video bg-[#14274B] rounded-lg overflow-hidden shadow-2xl">
              {!showVideo ? (
                <button 
                  onClick={() => setShowVideo(true)}
                  className="absolute inset-0 flex items-center justify-center group cursor-pointer"
                  data-testid="play-featured-video"
                >
                  <img 
                    src={`https://img.youtube.com/vi/${FEATURED_VIDEO.id}/maxresdefault.jpg`}
                    alt={FEATURED_VIDEO.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                    decoding="async"
                    width="1280"
                    height="720"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14274B]/80 to-transparent" />
                  <div className="relative z-10 w-20 h-20 rounded-full bg-[#2D78F6] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 z-10">
                    <p className="text-white text-lg font-semibold">{FEATURED_VIDEO.title}</p>
                    <p className="text-slate-300 text-sm">{FEATURED_VIDEO.speaker} • {FEATURED_VIDEO.duration}</p>
                  </div>
                </button>
              ) : (
                <iframe
                  src={`https://www.youtube.com/embed/${FEATURED_VIDEO.id}?autoplay=1`}
                  title={FEATURED_VIDEO.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <div>
              <blockquote className="text-xl md:text-2xl font-light text-[#14274B] leading-relaxed mb-6">
                "{t('landing.video.quote')}"
              </blockquote>
              <p className="text-slate-600 mb-8">{t('landing.video.description')}</p>
              <Link 
                to="/vision" 
                className="inline-flex items-center gap-2 text-sm font-medium text-[#2D78F6] hover:text-[#1A5BB5] transition-colors duration-200"
              >
                {t('landing.video.learnMore')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="audience-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-[#2D78F6] mb-4">{t('landing.audience.badge')}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">{t('landing.audience.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 hover:-translate-y-1 group">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-6 transition-colors duration-300" style={{ backgroundColor: `${a.color}15` }}>
                  <a.icon className="w-6 h-6 transition-colors duration-300" style={{ color: a.color }} />
                </div>
                <h3 className="text-lg font-semibold mb-3">{a.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#14274B] relative" data-testid="pillars-section">
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9IndoaXRlIi8+PC9zdmc+')]" />
        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-[#2D78F6] mb-4">{t('landing.pillars.badge')}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">{t('landing.pillars.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PILLARS.map((p) => (
              <div key={p.title} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 hover:bg-white/10 transition-all duration-300">
                <p.icon className="w-8 h-8 mb-4" style={{ color: p.color }} />
                <h3 className="text-xl font-semibold text-white mb-3">{p.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">{t('landing.cta.title')}</h2>
          <p className="text-base md:text-lg text-slate-600 max-w-xl mx-auto mb-10">{t('landing.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/programs" data-testid="cta-explore" className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium bg-[#2D78F6] text-white rounded-sm hover:bg-[#1A5BB5] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
              {t('landing.cta.explore')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contribute" data-testid="cta-contribute" className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium bg-transparent border border-[#14274B] text-[#14274B] rounded-sm hover:bg-[#14274B] hover:text-white transition-all duration-300">
              {t('landing.cta.teach')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
